import { jsPDF } from 'jspdf';
import autoTable, { UserOptions, RowInput, CellDef } from 'jspdf-autotable';
// @ts-ignore
import logo from '../assets/GLSLogo.png';

interface ChecklistItem {
    [key: string]: {
        yes: boolean;
        no: boolean;
        comment: string;
    };
}

interface FormData {
    technician: string;
    location: string;
    station: string;
    stationName: string;
    date: string;
    checklist: ChecklistItem;
}

// Define the table data type
type TableRow = (string | CellDef)[];
type TableData = RowInput[];

/**
 * Formats the checklist data for the PDF table
 */
const formatRadioDataForTable = (formData: FormData): TableData => {
    const sections = [
        {
            title: 'Cleaning',
            keys: [
                'STATION FREE OF DUST',
                'HAND SCANNER LENS CLEANED',
                'PAKR GLASS CLEANED',
            ],
        },
        {
            title: 'Inspection',
            keys: [
                'CHECK FOR STRUCTURAL DEFORMITY ON THE FRAME',
                'CHECK ALL BRACKETS ARE FULLY SECURED',
                'CHECK CABLES FOR KINKS AND CUTS, REPLACE IF NECESSARY',
                'CHECK FOR LOOSE CABLE, USE CABLE TIES TO HARNESS THE CABLES',
                'CABLE CONNECTIONS CHECKED',
            ],
        },
        {
            title: 'Hand Scanner',
            keys: [
                'TEST SCAN FUNCTIONALITY',
                'TEST SCAN FUNCTIONALITY_comment',
                'CHECK CABLES OR CRADLE TO ENSURE CONNECTIVITY',
            ],
        },
        {
            title: 'DIMENSIONER',
            keys: [
                'PASS DIMENSIONING TEST WITHIN +/- 0.2in',
                'TEST BOX LANE 1',
                'TEST BOX LANE 2',
                'TEST BOX LANE 3',
                'TEST BOX LANE 4',
                'TEST BOX LANE 5',
                'CHECK ENVISION SOFTWARE VERSION',
            ],
        },
        {
            title: 'SCALE',
            keys: [
                'PASS WEIGHT TEST WITHIN TOLERANCES',
            ],
        },
        {
            title: 'INVENTORY CHECK',
            keys: [
                'CHECK OUR INVENTORY LIST (SERIAL NUMBER)',
            ],
        },
        {
            title: 'TLX/CARGOSCAN',
            keys: [
                'CHECK THE HEX SCREW ON THE BERING',
                'SYSTEM FREE OF DUST',
                'CHECK CABLE CONNECTION',
            ],
        },
        // Add more sections as needed
    ];

    const data: TableData = [];
    sections.forEach((section) => {
        data.push([
            { content: section.title, colSpan: 3, styles: { halign: 'left', fontStyle: 'bold' } },
        ]);
        section.keys.forEach((key) => {
            if (!key.endsWith('_comment')) { // This check can remain to avoid repeating comment keys
                const title = key;
                const isChecked = formData.checklist[key]?.yes;
                // Directly access the `comment` property without forming a separate key
                const commentData = formData.checklist[key]?.comment ?? '';
                data.push([title, isChecked ? 'Yes' : 'No', commentData]);
            }
        });
    });
    return data;
};

/**
 * Generates a PDF from the inspection form data
 */
export const generatePDF = (formData: FormData): Blob => {
    // @ts-ignore - jsPDF constructor options are not properly typed
    const doc = new jsPDF({
        compress: true,
        putOnlyUsedFonts: true,
        precision: 2
    }) as jsPDF & {
        internal: {
            getNumberOfPages: () => number;
            pageSize: { height: number };
        };
    };

    // Draw a rectangle at the top for the header
    doc.setFillColor(1, 19, 179);
    doc.rect(10, 10, 190, 30, 'F');

    // Calculate logo positioning
    const headerCenterY = 10 + (30 / 2);
    const logoStartY = headerCenterY - (15 / 2);
    const logoStartX = 190 - 30 - 15;

    // Add the logo to the header with compression
    doc.addImage(logo, 'PNG', logoStartX, logoStartY, 30, 18, undefined, 'FAST');

    // Use a single font throughout to reduce file size
    doc.setFont('helvetica', 'normal');

    // Header text
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('Quarterly Inspection Report', 15, 25);

    // Reset for content
    doc.setFontSize(10); // Reduced font size
    doc.setTextColor(0, 0, 0);

    // Calculate quarters
    const dateParts = formData.date.split('-');
    const month = parseInt(dateParts[1]);
    const currentQuarter = Math.ceil(month / 3);

    const rightAlignX = 190;
    const padding = 10;

    const getStringWidth = (text: string, fontSize = 10) => {
        doc.setFontSize(fontSize);
        return doc.getTextWidth(text);
    };

    const rightAlignDateX = rightAlignX - getStringWidth('Date: ' + formData.date) - padding;
    const rightAlignQuarterX = rightAlignX - getStringWidth('Quarter: Q' + currentQuarter) - padding;

    // Display info
    doc.text('Technician: ' + formData.technician, 15, 50);
    doc.text('Location: ' + formData.location, 15, 60);
    doc.text('Station: ' + formData.stationName, 15, 70);
    doc.text('Date: ' + formData.date, rightAlignDateX, 50);
    doc.text('Quarter: Q' + currentQuarter, rightAlignQuarterX, 60);

    const tableData = formatRadioDataForTable(formData);

    // Optimize table settings
    autoTable(doc, {
        startY: 80,
        theme: 'striped',
        headStyles: {
            fillColor: [1, 19, 179],
            fontSize: 10,
            cellPadding: 2
        },
        bodyStyles: {
            fontSize: 9,
            cellPadding: 2
        },
        columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 20 },
            2: { cellWidth: 'auto' }
        },
        head: [['Checklist Item', 'Yes/No', 'Comments']],
        body: tableData,
        didDrawPage: (data) => {
            // Add page numbers
            const str = 'Page ' + doc.internal.getNumberOfPages();
            doc.setFontSize(8);
            doc.text(str, 15, doc.internal.pageSize.height - 10);
        }
    });

    // Return the compressed PDF
    return doc.output('blob') as Blob;
}; 